import React from 'react'
import {
    Box,
    Typography,
    Paper,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { Code as CodeIcon } from '@mui/icons-material'

const ServerActionPreview = ({ serverAction, fields = [] }) => {
    const formatJson = (obj) => {
        try {
            return JSON.stringify(obj, null, 2)
        } catch (err) {
            return String(obj)
        }
    }

    if (!serverAction) return null

    const headers = serverAction.headers || {}
    const payload = serverAction.payload || {}

    // Extract field tokens from payload and headers
    const extractTokens = (obj, prefix = '') => {
        const tokens = []
        if (typeof obj === 'object' && obj !== null) {
            Object.entries(obj).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    const matches = value.matchAll(/\{\{([^}]+)\}\}/g)
                    for (const match of matches) {
                        tokens.push({
                            path: prefix ? `${prefix}.${key}` : key,
                            token: match[1],
                            value: value,
                        })
                    }
                } else if (typeof value === 'object') {
                    tokens.push(...extractTokens(value, prefix ? `${prefix}.${key}` : key))
                }
            })
        }
        return tokens
    }

    const payloadTokens = extractTokens(payload)
    const headerTokens = extractTokens(headers)
    const allTokens = [...payloadTokens, ...headerTokens]

    return (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CodeIcon color="primary" />
                <Typography variant="h6">Server Action Preview</Typography>
            </Box>

            {/* HTTP Request Details */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Request Configuration
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Chip label={`Method: ${serverAction.http_request_method || 'POST'}`} color="primary" />
                    <Chip label={`Sequence: ${serverAction.sequence || 1}`} variant="outlined" />
                </Box>
                <Typography variant="body2" color="textSecondary">
                    Endpoint:
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        p: 1,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        wordBreak: 'break-all',
                    }}
                >
                    {serverAction.endpoint || 'Not configured'}
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Headers */}
            {Object.keys(headers).length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Headers
                    </Typography>
                    <Box
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            p: 1.5,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                        }}
                    >
                        {formatJson(headers)}
                    </Box>
                </Box>
            )}

            {/* Payload */}
            {Object.keys(payload).length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Payload (Request Body)
                    </Typography>
                    <Box
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            p: 1.5,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                        }}
                    >
                        {formatJson(payload)}
                    </Box>
                </Box>
            )}

            {/* Dynamic Field Mappings */}
            {allTokens.length > 0 && (
                <Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Dynamic Fields Detected ({allTokens.length})
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Token</TableCell>
                                    <TableCell>Template Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allTokens.map((token, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {token.path}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={token.token} size="small" color="secondary" />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {token.value}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {Object.keys(headers).length === 0 && Object.keys(payload).length === 0 && (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                    No headers or payload configured yet
                </Typography>
            )}
        </Paper>
    )
}

export default ServerActionPreview
